import { useState } from 'react';
import {
  MIN_QUESTIONS_PER_ROUND,
  MAX_QUESTIONS_PER_ROUND,
  type AdminQuizView,
  type AdminRoundView,
} from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';

export function QuizEditor({
  token,
  quiz,
  onChange,
}: {
  token: string;
  quiz: AdminQuizView;
  onChange: (q: AdminQuizView) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = async (path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await api<AdminQuizView>(path, { method, token, body }));
    } catch (err) {
      // Was try/finally with no catch: a failed reorder or a rejected round
      // change did nothing at all and said nothing, which reads as the app
      // ignoring you.
      setError(err instanceof ApiError ? err.message : 'That change did not save.');
    } finally {
      setBusy(false);
    }
  };

  // Frozen while a host is part-way through this quiz. The server refuses these
  // changes anyway; this is so the builder says why instead of throwing an error
  // at somebody who was only trying to fix a typo.
  const frozen = quiz.inSession !== null;
  const locked = busy || frozen;

  const errors = quiz.validation.issues.filter((i) => i.level === 'ERROR');
  const warnings = quiz.validation.issues.filter((i) => i.level === 'WARNING');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        {/* The name is what the room sees on their phones, so it has to be
            fixable after the fact -- "Star Wars Night" beats "Untitled". */}
        <input
          defaultValue={quiz.name}
          key={quiz.id}
          onBlur={(e) => {
            const name = e.target.value.trim();
            if (name && name !== quiz.name) void call(`/api/admin/quizzes/${quiz.id}`, 'PATCH', { name });
          }}
          aria-label="Quiz name"
          className="min-w-0 flex-1 rounded-lg bg-transparent px-2 py-1 text-xl font-bold
            outline-none hover:bg-ink-2 focus:bg-ink-2 focus:ring-1 focus:ring-sky"
        />
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold ${
            quiz.validation.ready ? 'bg-go/20 text-go' : 'bg-stop/20 text-stop'
          }`}
        >
          {quiz.validation.ready ? 'Ready to run' : `${errors.length} to fix`}
        </span>
        <button
          disabled={locked}
          onClick={async () => {
            if (!confirm(`Delete the quiz "${quiz.name}" and its rounds? The questions stay in the bank.`)) return;
            try {
              await api(`/api/admin/quizzes/${quiz.id}`, { method: 'DELETE', token });
              // Nothing left to show, so hand the page back to the picker.
              location.reload();
            } catch (err) {
              // The server refuses to delete a quiz an event has been run from.
              alert(err instanceof ApiError ? err.message : 'Could not delete that quiz.');
            }
          }}
          className="shrink-0 text-sm text-stop underline disabled:opacity-40"
        >
          Delete quiz
        </button>
      </div>

      {error && (
        <p className="rounded-xl bg-stop/15 p-3 text-sm text-stop" role="alert">
          {error}
        </p>
      )}

      {quiz.inSession && (
        <p className="rounded-xl bg-flag/15 p-3 text-sm text-flag" role="status">
          <b>{quiz.inSession.eventName}</b> is playing this quiz right now, so the rounds and
          questions are frozen until it ends. Every phone has already downloaded the quiz, so
          changing it mid-round would leave them holding something else. You can still fix the
          name.
        </p>
      )}

      {/* The same validateQuiz the server enforces with, so this is the verdict,
          not a preview of one. */}
      {(errors.length > 0 || warnings.length > 0) && (
        <ul className="space-y-1 rounded-xl bg-ink-2 p-4 text-sm">
          {errors.map((i, n) => (
            <li key={n} className="text-stop">✗ {i.message}</li>
          ))}
          {warnings.map((i, n) => (
            <li key={n} className="text-flag">! {i.message}</li>
          ))}
        </ul>
      )}

      {quiz.rounds.map((round, index) => (
        <RoundCard
          key={round.id}
          round={round}
          index={index}
          total={quiz.rounds.length}
          busy={locked}
          call={call}
          quizId={quiz.id}
          allRoundIds={quiz.rounds.map((r) => r.id)}
        />
      ))}

      <button
        disabled={locked}
        onClick={() => {
          const title = prompt('Round title (e.g. "Music Intros")');
          if (title) void call(`/api/admin/quizzes/${quiz.id}/rounds`, 'POST', { title });
        }}
        className="min-h-12 w-full rounded-xl border border-dashed border-ink-3 text-muted hover:border-muted"
      >
        + Add round
      </button>

      {quiz.validation.ready && <CreateEvent token={token} quizId={quiz.id} />}
    </div>
  );
}

function RoundCard({
  round,
  index,
  total,
  busy,
  call,
  quizId,
  allRoundIds,
}: {
  round: AdminRoundView;
  index: number;
  total: number;
  busy: boolean;
  call: (path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown) => Promise<void>;
  quizId: string;
  allRoundIds: string[];
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const count = round.questions.length;
  const sizeOk = count >= MIN_QUESTIONS_PER_ROUND && count <= MAX_QUESTIONS_PER_ROUND;

  /** Drag to reorder. The server writes it in two passes; here we just send the order. */
  async function drop(targetQuestionId: string) {
    if (!dragging || dragging === targetQuestionId) return;
    const ids = round.questions.map((q) => q.question.id);
    const from = ids.indexOf(dragging);
    const to = ids.indexOf(targetQuestionId);
    ids.splice(to, 0, ...ids.splice(from, 1));
    setDragging(null);
    await call(`/api/admin/rounds/${round.id}/reorder`, 'POST', { ids });
  }

  const moveRound = async (delta: number) => {
    const ids = [...allRoundIds];
    const to = index + delta;
    if (to < 0 || to >= ids.length) return;
    ids.splice(to, 0, ...ids.splice(index, 1));
    await call(`/api/admin/quizzes/${quizId}/reorder`, 'POST', { ids });
  };

  return (
    <section className="rounded-xl bg-ink-2 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted">Round {index + 1}</span>
        <input
          defaultValue={round.title}
          disabled={busy}
          onBlur={(e) =>
            e.target.value !== round.title &&
            void call(`/api/admin/rounds/${round.id}`, 'PATCH', { title: e.target.value })
          }
          className="flex-1 min-w-40 rounded-lg bg-ink px-3 py-2 font-semibold outline-none focus:ring-1 focus:ring-sky"
        />
        <label className="flex items-center gap-1.5 text-xs text-muted">
          Points
          <input
            type="number"
            step={1}
            min={1}
            defaultValue={round.pointsMultiplierPct}
            disabled={busy}
            onBlur={(e) =>
              void call(`/api/admin/rounds/${round.id}`, 'PATCH', {
                // Integer percentages only: 200 for double points, never 2.0.
                pointsMultiplierPct: Math.round(Number(e.target.value)),
              })
            }
            className="w-20 rounded-lg bg-ink px-2 py-1.5 tabular-nums outline-none"
          />
          %
        </label>
        <label className="flex items-center gap-1.5 text-xs text-muted">
          Timer
          <input
            type="number"
            min={5}
            placeholder="event"
            defaultValue={round.timeLimitSec ?? ''}
            disabled={busy}
            onBlur={(e) =>
              void call(`/api/admin/rounds/${round.id}`, 'PATCH', {
                timeLimitSec: e.target.value ? Number(e.target.value) : null,
              })
            }
            className="w-20 rounded-lg bg-ink px-2 py-1.5 tabular-nums outline-none"
          />
          s
        </label>
        <span className={`text-xs ${sizeOk ? 'text-muted' : 'text-stop'}`}>
          {count}/{MIN_QUESTIONS_PER_ROUND}&ndash;{MAX_QUESTIONS_PER_ROUND}
        </span>
        <div className="flex gap-1">
          <button disabled={busy || index === 0} onClick={() => void moveRound(-1)}
            className="rounded px-2 py-1 text-muted hover:bg-ink-3 disabled:opacity-30">↑</button>
          <button disabled={busy || index === total - 1} onClick={() => void moveRound(1)}
            className="rounded px-2 py-1 text-muted hover:bg-ink-3 disabled:opacity-30">↓</button>
          <button
            disabled={busy}
            onClick={() =>
              confirm(`Delete round "${round.title}"?`) &&
              void call(`/api/admin/rounds/${round.id}`, 'DELETE')
            }
            className="rounded px-2 py-1 text-stop hover:bg-ink-3"
          >
            ×
          </button>
        </div>
      </div>

      <ol className="mt-3 space-y-1">
        {round.questions.map((rq, i) => (
          <li
            key={rq.roundQuestionId}
            draggable={!busy}
            onDragStart={() => !busy && setDragging(rq.question.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => void drop(rq.question.id)}
            className={`flex cursor-grab items-center gap-3 rounded-lg bg-ink px-3 py-2 text-sm
              ${dragging === rq.question.id ? 'opacity-40' : ''}`}
          >
            <span className="w-5 tabular-nums text-muted">{i + 1}</span>
            <span className="flex-1 truncate">{rq.question.prompt}</span>
            <span className="text-xs text-muted">{rq.question.format === 'FREE_TEXT' ? 'text' : 'choice'}</span>
            <span className="max-w-40 truncate text-xs text-go">{rq.question.correctAnswer}</span>
            <button
              disabled={busy}
              onClick={() =>
                void call(
                  `/api/admin/rounds/${round.id}/questions/${rq.question.id}`,
                  'DELETE',
                )
              }
              className="text-muted hover:text-stop disabled:opacity-30"
            >
              ×
            </button>
          </li>
        ))}
        {count === 0 && (
          <li className="py-3 text-center text-sm text-muted">
            Add questions from the bank on the right.
          </li>
        )}
      </ol>
    </section>
  );
}

type JoinMode = 'TICKET' | 'CODE' | 'OPEN';

const MODES: { mode: JoinMode; label: string; blurb: string }[] = [
  {
    mode: 'TICKET',
    label: 'Paid night',
    blurb: 'One code per player, issued at the door once they have paid.',
  },
  {
    mode: 'CODE',
    label: 'Free, shared code',
    blurb: 'One code on the table admits everybody. Free.',
  },
  {
    mode: 'OPEN',
    label: 'Practice quiz',
    blurb:
      'No code at all -- anyone with the link taps join. Runs whenever no real night is set up.',
  },
];

function CreateEvent({ token, quizId }: { token: string; quizId: string }) {
  const [result, setResult] = useState<{
    joinCode: string | null;
    joinMode: JoinMode;
    staffPins: Record<string, string>;
  } | null>(null);
  const [mode, setMode] = useState<JoinMode>('TICKET');
  const [feePence, setFeePence] = useState(300);
  const [name, setName] = useState('Tonight');
  const [when, setWhen] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Only a paid night has a fee; the other two are free by definition.
  const effectiveFee = mode === 'TICKET' ? feePence : 0;

  if (result) {
    return (
      <div className="rounded-xl bg-go/15 p-4 text-sm">
        <p className="font-semibold text-go">Event created.</p>
        {result.joinCode ? (
          <p className="mt-2">
            Join code <span className="font-mono text-lg">{result.joinCode}</span>
          </p>
        ) : (
          <p className="mt-2 text-muted">
            {result.joinMode === 'OPEN'
              ? 'No join code: players just tap join.'
              : 'Codes are issued per player at the door.'}
          </p>
        )}
        <p className="mt-1">
          Host PIN <b>{result.staffPins.host}</b> &middot; Manager PIN{' '}
          <b>{result.staffPins.manager}</b>
        </p>
        <p className="mt-2 text-muted">
          These PINs are shown once and stored hashed. Write them down now.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl bg-ink-2 p-4">
      <div className="grid gap-2 sm:grid-cols-3">
        {MODES.map((m) => (
          <button
            key={m.mode}
            onClick={() => setMode(m.mode)}
            className={`rounded-lg p-3 text-left text-sm ${
              mode === m.mode ? 'bg-sky text-ink' : 'bg-ink text-muted hover:bg-ink-3'
            }`}
          >
            <span className="block font-semibold">{m.label}</span>
            <span className="mt-1 block text-xs opacity-80">{m.blurb}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-muted">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="ml-2 w-48 rounded-lg bg-ink px-2 py-1.5 outline-none"
          />
        </label>

        <label className="text-sm text-muted">
          When
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="ml-2 rounded-lg bg-ink px-2 py-1.5 outline-none"
          />
          {/* The practice quiz sits open with no date, so this stays optional. */}
          <span className="ml-2 text-xs">optional</span>
        </label>

        {mode === 'TICKET' && (
          <label className="text-sm text-muted">
            Entry fee (pence)
            <input
              type="number"
              min={0}
              step={1}
              value={feePence}
              onChange={(e) => setFeePence(Math.max(0, Math.round(Number(e.target.value))))}
              className="ml-2 w-24 rounded-lg bg-ink px-2 py-1.5 tabular-nums outline-none"
            />
            <span className="ml-2 text-xs">£{(feePence / 100).toFixed(2)} per person</span>
          </label>
        )}

        <button
          disabled={!name.trim()}
          onClick={async () => {
            setError(null);
            try {
              setResult(
                await api('/api/admin/events', {
                  method: 'POST',
                  token,
                  body: {
                    quizId,
                    name: name.trim(),
                    entryFeePence: effectiveFee,
                    joinMode: mode,
                    scheduledFor: when ? new Date(when).toISOString() : null,
                  },
                }),
              );
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Could not create the event.');
            }
          }}
          className="ml-auto min-h-11 rounded-lg bg-go px-5 font-semibold text-ink disabled:opacity-40"
        >
          Create event
        </button>
      </div>
      {error && <p className="text-sm text-stop">{error}</p>}
    </div>
  );
}
